/* eslint-disable @typescript-eslint/camelcase */
import { inject } from 'inversify-props'
import API from '@core/services/API'
import { Component, Vue } from 'vue-property-decorator'
import AppDatatable from '@core/components/AppDatatable.vue'
import { IDateService } from '@master/services/IDateService'
import { UIService } from '@core/services/UIService'
import { SharedVariable } from '@core/model/SharedVariable'
import { omit } from 'lodash/fp'

export interface Pending {
  _oid: string;
  ma_name: string;
  ma_nric: string;
  adviser_name: string;
  date_pdf_submission: string;
  time_pdf_submission: string;
  consolidatedStatus: string;
  redirect: string;
  keyword: string;
}
export interface PendingSubmission {
  _oid: string;
  ma_name: string;
  ma_nric: string;
  adviser_name: string;
  date_supervisor_decide: string;
  time_supervisor_decide: string;
  consolidatedStatus: string;
  redirect: string;
  keyword: string;
  pdf_status: string;
}
export interface Approved {
  _oid: string;
  ma_name: string;
  ma_nric: string;
  adviser_name: string;
  date_supervisor_decide: string;
  time_supervisor_decide: string;
  consolidatedStatus: string;
  redirect: string;
  keyword: string;
}
export interface Rejected {
  _oid: string;
  ma_name: string;
  ma_nric: string;
  adviser_name: string;
  date_supervisor_decide: string;
  time_supervisor_decide: string;
  consolidatedStatus: string;
  redirect: string;
  keyword: string;
}
@Component({
  components: {
    AppDatatable
  }
})
export class HomeSupervisorMixin extends Vue {
  @inject()
  protected dateService: IDateService

  @inject()
  protected $api: API

  uiService: UIService

  @inject()
  protected sharedVariable: SharedVariable

  protected pending = 0
  protected pendingColumns: Array<object> = [
    { label: 'Client 1 Name', field: 'ma_name' },
    { label: 'NRIC / Passport / FIN', field: 'ma_nric' },
    { label: 'Adviser Name', field: 'adviser_name' },
    { label: 'Date of Submission', field: 'date_pdf_submission' },
    { label: 'Time of Submission', field: 'time_pdf_submission' },
    { label: 'E-Sub Status', field: 'consolidatedStatus' }]

  protected pendingSubmissionColumns: Array<object> = [
    { label: 'Client 1 Name', field: 'ma_name' },
    { label: 'NRIC / Passport / FIN', field: 'ma_nric' },
    { label: 'Adviser Name', field: 'adviser_name' },
    { label: 'Date of A/R(Sup)', field: 'date_supervisor_decide' },
    { label: 'Time of A/R(Sup)', field: 'time_supervisor_decide' },
    { label: 'E-Sub Status', field: 'consolidatedStatus' }]

  protected approvedColumns: Array<object> = [
    { label: 'Client 1 Name', field: 'ma_name' },
    { label: 'NRIC / Passport / FIN', field: 'ma_nric' },
    { label: 'Adviser Name', field: 'adviser_name' },
    { label: 'Date of A/R(Sup)', field: 'date_supervisor_decide' },
    { label: 'Time of A/R(Sup)', field: 'time_supervisor_decide' },
    { label: 'E-Sub Status', field: 'consolidatedStatus' }]

  protected rejectedColumns: Array<object> = [
    { label: 'Client 1 Name', field: 'ma_name' },
    { label: 'NRIC / Passport / FIN', field: 'ma_nric' },
    { label: 'Adviser Name', field: 'adviser_name' },
    { label: 'Date of A/R(Sup)', field: 'date_supervisor_decide' },
    { label: 'Time of A/R(Sup)', field: 'time_supervisor_decide' },
    { label: 'E-Sub Status', field: 'consolidatedStatus' }]

  protected pendingRows: Pending[] = []
  protected pendingSubmissionRows: PendingSubmission[] = []
  protected pendingAdminRows: Pending[] = []
  protected approvedRows: Approved[] = []
  protected rejectedRows: Rejected[] = []
  protected pendingKeys = ['_oid', 'ma_name', 'ma_nric', 'adviser_name', 'date_pdf_submission', 'date_pdf_submission', 'consolidatedStatus']
  protected pendingSubmissionKeys = ['_oid', 'ma_name', 'ma_nric', 'adviser_name', 'date_supervisor_decide', 'date_supervisor_decide', 'consolidatedStatus', 'pdf_status']
  protected approvedKeys = ['_oid', 'ma_name', 'ma_nric', 'adviser_name', 'date_supervisor_decide', 'date_supervisor_decide', 'consolidatedStatus']
  protected rejectedKeys = ['_oid', 'ma_name', 'ma_nric', 'adviser_name', 'date_supervisor_decide', 'date_supervisor_decide', 'consolidatedStatus']
  protected tabBottom = 'pending'
  protected txtSearchPending = ''
  protected txtSearchBottom = ''
  protected accountId: string
  protected isSuperUser: boolean

  protected showEsubPendingTab = false

  created (): void {
    // take injected UIService and set it to local
    this.uiService = this.sharedVariable.uiService

    this.isSuperUser = this.$store.getters.currentUser.isSuperUser
    this.accountId = this.$store.getters.currentUser.accountID
    // eslint-disable-next-line @typescript-eslint/camelcase
    this.showEsubPendingTab = this.sharedVariable?.sysInfo?.esub_ntuc
    if (!this.showEsubPendingTab) {
      this.tabBottom = 'pending-admin'
    }
    this.getPendingList()
    this.getPendingSubmissionList()
    this.getPendingAdminList()
    this.getApprovedList()
    this.getRejectedList()
  }

  renameObjectKeys (list: Array<object>, keys: Array<string>): Array<object> {
    const newList = list.map(function (data) {
      const keyValues = Object.keys(data).map(function (key) {
        const newKey = keys[key]
        return { [newKey]: data[key] }
      })
      return Object.assign({}, ...keyValues)
    })
    return newList
  }

  async getPendingList () {
    let query: string
    let queryArgs: string
    if (this.isSuperUser) {
      query = 'pdf_status = ? AND adviser_finalised = ?'
      queryArgs = JSON.stringify(['adviser-signed', 'true'])
    } else {
      query = '(supervisor_oid = ? OR supervisor_proxy_oid = ?) AND pdf_status = ? AND adviser_finalised = ?'
      queryArgs = JSON.stringify([this.accountId, this.accountId, 'adviser-signed', 'true'])
    }
    const response = await this.$api.kycPdf.list({
      query: query,
      queryArgs: queryArgs,
      wildcardMode: 'suffix,prefix',
      orderBy: 'date_pdf_submission',
      headers: JSON.stringify(this.pendingKeys)
    })
    this.pendingRows = this.renameObjectKeys(response.data, this.pendingKeys) as Pending[]
    this.pendingRows = this.pendingRows.map((pending) => {
      pending.time_pdf_submission = this.dateService.format(pending.date_pdf_submission, 'HH:mm:ss')
      pending.date_pdf_submission = this.dateService.format(pending.date_pdf_submission, 'YYYY-MM-DD')
      pending.redirect = '/live/kyc-pdf/' + pending._oid
      pending.keyword = Object.values(omit('redirect', pending)).join(',').toLowerCase() // assign keyword with all object values except 'redirect' keys
      return pending
    })
    this.pending = this.pendingRows.length
  }

  async getPendingSubmissionList () {
    let query: string
    let queryArgs: string
    if (this.isSuperUser) {
      query = 'pdf_status = ?'
      queryArgs = JSON.stringify(['supervisor-approved'])
    } else {
      query = '(supervisor_oid = ? OR supervisor_proxy_oid = ?) AND pdf_status = ?'
      queryArgs = JSON.stringify([this.accountId, this.accountId, 'supervisor-approved'])
    }
    const response = await this.$api.kycPdf.list({
      query: query,
      queryArgs: queryArgs,
      wildcardMode: 'suffix,prefix',
      orderBy: 'date_supervisor_decide',
      headers: JSON.stringify(this.pendingSubmissionKeys)
    })
    this.pendingSubmissionRows = this.renameObjectKeys(response.data, this.pendingSubmissionKeys) as PendingSubmission[]
    this.pendingSubmissionRows = this.pendingSubmissionRows.filter(submission => {
      return submission.consolidatedStatus === 'Completed' && submission.pdf_status === 'submission-accepted'
    }).map((pending) => {
      pending.time_supervisor_decide = this.dateService.format(pending.date_supervisor_decide, 'HH:mm:ss')
      pending.date_supervisor_decide = this.dateService.format(pending.date_supervisor_decide, 'YYYY-MM-DD')
      pending.redirect = '/live/kyc-pdf/' + pending._oid
      pending.keyword = Object.values(omit('redirect', pending)).join(',').toLowerCase()
      return pending
    })
  }

  async getPendingAdminList () {
    let query: string
    let queryArgs: string
    if (this.isSuperUser) {
      query = 'pdf_status = ? AND adviser_finalised = ?'
      queryArgs = JSON.stringify(['supervisor-approved', 'true'])
    } else {
      query = '(supervisor_oid = ? OR supervisor_proxy_oid = ?) AND pdf_status = ? AND adviser_finalised = ?'
      queryArgs = JSON.stringify([this.accountId, this.accountId, 'supervisor-approved', 'true'])
    }
    const response = await this.$api.kycPdf.list({
      query: query,
      queryArgs: queryArgs,
      wildcardMode: 'suffix,prefix',
      orderBy: 'date_pdf_submission',
      headers: JSON.stringify(this.pendingKeys)
    })
    this.pendingAdminRows = this.renameObjectKeys(response.data, this.pendingKeys) as Pending[]
    this.pendingAdminRows = this.pendingAdminRows.map((pending) => {
      pending.time_pdf_submission = this.dateService.format(pending.date_pdf_submission, 'HH:mm:ss')
      pending.date_pdf_submission = this.dateService.format(pending.date_pdf_submission, 'YYYY-MM-DD')
      pending.redirect = '/live/kyc-pdf/' + pending._oid
      pending.keyword = Object.values(omit('redirect', pending)).join(',').toLowerCase() // assign keyword with all object values except 'redirect' keys
      return pending
    })
  }

  async getApprovedList () {
    let query: string
    let queryArgs: string
    if (this.isSuperUser) {
      query = 'pdf_status = ?'
      queryArgs = JSON.stringify(['submission-accepted'])
    } else {
      query = '(supervisor_oid = ? OR supervisor_proxy_oid = ?)  AND pdf_status = ?'
      queryArgs = JSON.stringify([this.accountId, this.accountId, 'submission-accepted'])
    }
    const response = await this.$api.kycPdf.list({
      query: query,
      queryArgs: queryArgs,
      wildcardMode: 'suffix,prefix',
      orderBy: 'date_supervisor_decide',
      headers: JSON.stringify(this.approvedKeys)
    })
    this.approvedRows = this.renameObjectKeys(response.data, this.approvedKeys) as Approved[]
    this.approvedRows = this.approvedRows.map((approved) => {
      approved.time_supervisor_decide = this.dateService.format(approved.date_supervisor_decide, 'HH:mm:ss')
      approved.date_supervisor_decide = this.dateService.format(approved.date_supervisor_decide, 'YYYY-MM-DD')
      approved.redirect = '/live/kyc-pdf/' + approved._oid
      approved.keyword = Object.values(omit('redirect', approved)).join(',').toLowerCase()
      return approved
    })
  }

  async getRejectedList () {
    let query: string
    let queryArgs: string
    if (this.isSuperUser) {
      query = '(pdf_status = ? OR pdf_status = ?)'
      queryArgs = JSON.stringify(['submission-rejected', 'supervisor-rejected'])
    } else {
      query = '(supervisor_oid = ? OR supervisor_proxy_oid = ?) AND (pdf_status = ? OR pdf_status = ?)'
      queryArgs = JSON.stringify([this.accountId, this.accountId, 'submission-rejected', 'supervisor-rejected'])
    }
    const response = await this.$api.kycPdf.list({
      query: query,
      queryArgs: queryArgs,
      wildcardMode: 'suffix,prefix',
      orderBy: 'date_supervisor_decide',
      headers: JSON.stringify(this.approvedKeys)
    })
    this.rejectedRows = this.renameObjectKeys(response.data, this.rejectedKeys) as Rejected[]
    this.rejectedRows = this.rejectedRows.map((rejected) => {
      rejected.time_supervisor_decide = this.dateService.format(rejected.date_supervisor_decide, 'HH:mm:ss')
      rejected.date_supervisor_decide = this.dateService.format(rejected.date_supervisor_decide, 'YYYY-MM-DD')
      rejected.redirect = '/live/kyc-pdf/' + rejected._oid
      rejected.keyword = Object.values(omit('redirect', rejected)).join(',').toLowerCase()
      return rejected
    })
  }

  changeTabBottom (event: Event, tab: string): void {
    const div = event.target as HTMLDivElement
    if (div.classList.contains('active')) {
      return
    }
    this.tabBottom = tab
    this.txtSearchBottom = ''
  }
}
